import { useState, type ReactNode } from 'react';
import { TooltipProvider } from '@monksflow/monks-ui';
import clsx from 'clsx';
import { File, FileCode, Film, FolderPlus } from 'lucide-react';
import { FileAssetWithUrl } from '@/features/chats/model/chats.types';
import { useFileAttachments } from '@/features/chats/model/useFileAttachments';
import { ImagePreviewV2 } from '@/shared/ui/ImageTag/ImagePreview';
import { AttachmentAddButton, AttachmentChip, AttachmentRemoveButton } from '../../AttachmentChip';
import { SaveToProjectPopover } from '@/shared/ui';
import { SPECIAL_AGENT_IDS } from '@/entities/agent';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useSaveToProjectEnabled } from '@/shared/hooks';
import { saveFileArtifact } from '@/features/chats/api/saveFileArtifact';
import { useTranslation } from 'react-i18next';

// The "Add to library" save action is scoped to the translation agent's output
// (the only agent whose deliverable is a file the user saves). Other agents'
// file attachments keep just the "add to chat" affordance.

interface ChatMessageFilesProps {
  files: FileAssetWithUrl[];
  type: 'user' | 'assistant';
  /** Agent that produced an assistant message; gates the "Add to library" action. */
  agentId?: string;
}

/* eslint-disable i18next/no-literal-string -- MIME type fragments */
const THUMBNAIL_CODE_MIME_PARTS = [
  'html',
  'javascript',
  'typescript',
  'json',
  'xml',
  'css',
  'text/plain',
];
/* eslint-enable i18next/no-literal-string */

function FileTypeIcon(props: { mimeType: string }) {
  if (props.mimeType.includes('video')) {
    return <Film className="size-4 text-foreground" />;
  }
  if (THUMBNAIL_CODE_MIME_PARTS.some((part) => props.mimeType.includes(part))) {
    return <FileCode className="size-4 text-foreground" />;
  }
  return <File className="size-4 text-foreground" />;
}

/**
 * Image attachment: a thumbnail that opens a preview on click. Self-provides a
 * TooltipProvider + group so the re-attach/remove control (and its tooltip)
 * behave like the pill chips.
 */
function MessageImageChip(props: { url?: string; filename: string; trailing: ReactNode }) {
  const { t } = useTranslation('chats');

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  return (
    <TooltipProvider>
      <div className="group relative inline-flex" data-testid="message-image-chip">
        <button
          type="button"
          aria-label={t('chats.chatMessageFiles.preview', { filename: props.filename })}
          onClick={() => props.url && setIsPreviewOpen(true)}
          className="size-20 overflow-hidden rounded-lg border border-border-light bg-surface transition-colors hover:border-border"
        >
          <img src={props.url} alt={props.filename} className="h-full w-full object-cover" />
        </button>
        {props.trailing}
        <ImagePreviewV2
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          filename={props.filename}
          url={props.url}
        />
      </div>
    </TooltipProvider>
  );
}

/**
 * "Add to library" control, hover-revealed and stacked just below the
 * add/remove-from-chat toggle at the chip's top-right. Opens the save-to-project
 * popover; only rendered for saveable assistant files.
 */
function SaveToLibraryButton(props: { workspaceId: string; file: FileAssetWithUrl }) {
  const { t } = useTranslation('chats');

  return (
    <span className="absolute -right-2 top-3 opacity-0 transition-opacity group-hover:opacity-100">
      <SaveToProjectPopover
        resourceId={props.file.assetId}
        workspaceId={props.workspaceId}
        onMintArtifact={() => saveFileArtifact(props.workspaceId, props.file)}
      >
        <button
          type="button"
          aria-label={t('chats.chatMessageFiles.addFileToLibrary', {
            fileName: props.file.metadata.fileName,
          })}
          title={t('chats.chatMessageFiles.addToLibrary')}
          data-testid="add-to-library-button"
          // Stop propagation so opening the popover doesn't also trigger the
          // chip's own click (image preview / open file).
          onClick={(e) => e.stopPropagation()}
          className="flex size-4 items-center justify-center rounded-full border border-border-light bg-background shadow-sm hover:bg-accent"
        >
          <FolderPlus className="size-2.5" />
        </button>
      </SaveToProjectPopover>
    </span>
  );
}

/**
 * The file attachments shown on a message. Mirrors the project-context chips by
 * reusing AttachmentChip for documents and a thumbnail for images, all on
 * monks-ui. Each chip can be re-attached to the next message or dropped;
 * saveable assistant files (translation output) also get an "Add to library"
 * action.
 */
export const ChatMessageFiles = ({ files, type, agentId }: ChatMessageFilesProps) => {
  const { t } = useTranslation('chats');

  const { files: inputFiles, handleFileReference, handleRemoveFile } = useFileAttachments({});
  const workspaceId = useCurrentWorkspaceId();
  const saveEnabled = useSaveToProjectEnabled();

  // Only assistant-produced translation files get "Add to library" — gated on the
  // flag and an available workspace (absent outside a workspace context).
  const showSaveAction =
    saveEnabled &&
    type === 'assistant' &&
    agentId === SPECIAL_AGENT_IDS.TRANSLATION &&
    !!workspaceId;

  if (files.length === 0) return null;

  return (
    <div
      className={clsx(
        'flex flex-wrap gap-2 py-1.5',
        type === 'user' ? 'justify-end' : 'justify-start',
      )}
    >
      {files.map((file) => {
        const isReferenced = inputFiles.some((f) => f.assetId === file.assetId);
        const fileName = file.metadata.fileName || file.assetId;

        const toggle = isReferenced ? (
          <AttachmentRemoveButton
            ariaLabel={t('chats.chatMessageFiles.removeFromNextMessage', { fileName })}
            testId="remove-from-message-button"
            onClick={() => handleRemoveFile(file.assetId)}
          />
        ) : (
          <AttachmentAddButton
            ariaLabel={t('chats.chatMessageFiles.addToNextMessage', { fileName })}
            testId="add-to-message-button"
            onClick={() => handleFileReference(file)}
          />
        );

        const trailing = (
          <>
            {showSaveAction && workspaceId && (
              <SaveToLibraryButton workspaceId={workspaceId} file={file} />
            )}
            {toggle}
          </>
        );

        if (file.mimeType.startsWith('image/')) {
          return (
            <MessageImageChip
              key={file.assetId}
              url={file.url}
              filename={fileName}
              trailing={trailing}
            />
          );
        }

        return (
          <AttachmentChip
            key={`${file.assetId}-${isReferenced ? 'referenced' : 'addable'}`}
            name={fileName}
            size="lg"
            leading={<FileTypeIcon mimeType={file.mimeType} />}
            onClick={
              file.url ? () => window.open(file.url, '_blank', 'noopener,noreferrer') : undefined
            }
            trailing={trailing}
          />
        );
      })}
    </div>
  );
};
